/**
 * TRAZABILIDAD (FASE 9.4 · reorganizada en 9.4-B) — lotes, series y caducidades
 *
 * Lotes y series NO son módulos de primer nivel: son la granularidad del
 * stock. Se consultan en Stock («Por lote» / «Por serie», ?tab=lotes|series),
 * se usan dentro de cada línea de Movimientos y aparecen en la ficha del
 * artículo que los controla. Aquí viven las piezas y las fichas:
 *   /inventario/lotes/:id      ficha: Información (stock por almacén) · Series · Histórico · Cambios
 *   /inventario/series/:id     ficha: Información (ubicación) · Histórico · Cambios
 *   (/inventario/lotes y /inventario/series redirigen a Stock)
 *
 * Y las piezas que usan Stock, Movimientos e Inventarios: selector de lote
 * con alta en línea, editor de series (pegar/escanear, duplicados, N/N),
 * etiqueta de caducidad y «Ver detalle» del stock.
 *
 * Nada de esto edita stock: el saldo por lote y la ubicación de una serie
 * salen del Histórico (ledger). Un lote caducado se ADVIERTE, no se bloquea.
 *
 * F13.2: estado BLOQUEADO (sólo ADMIN/CONSULTOR, con motivo: no sale, no se
 * consume ni se traslada), proveedor, recepción derivada del ledger,
 * identificadores externos del lote y etiqueta interna (vista previa + PDF).
 */
import { useState, type ReactNode } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';

import {
  api,
  FalloApi,
  type CaducidadLote,
  type DetalleStock,
  type EstadoLote,
  type TipoIdentificadorLote,
  type EventoTrazabilidad,
  type LoteDetalle,
  type LoteResumen,
  type OpcionArticuloStock,
  type OpcionLote,
  type OpcionSerie,
  type SerieDetalle,
  type SerieResumen,
} from '../api.ts';
import { BarraAcciones, Error as ErrorCaja, Estado, fecha, fechaHora, FiltrosResponsive, Modal, Panel, useDatos, Vacio } from '../componentes/base.tsx';
import { CambiosEvento } from '../componentes/cambios-historial.tsx';
import { estadoArticulo } from '../estados.ts';
import { Buscador } from '../componentes/buscador.tsx';
import { DialogoEtiquetas, PanelEtiqueta, type ElementoEtiqueta } from '../componentes/etiquetas.tsx';
import { PieHistorial, useHistorial } from '../componentes/historial.tsx';
import { Shell } from '../componentes/shell.tsx';
import { useSesion } from '../contexto.tsx';
import { erroresDe, Ledger, nombreArticulo, Paginacion, Pestanas } from './stock.tsx';
import { codigoAlmacen } from '../componentes/codigo-articulo.tsx';

const POR_PAGINA = 50;

/** FASE 9.4-B — los conceptos, dichos igual en toda la pantalla. */
export const TEXTO_LOTE_SIN_STOCK = 'Crear un lote no crea stock: el lote tendrá existencias cuando se confirme una entrada con él.';
export const TEXTO_SERIE_SIN_STOCK = 'Dar de alta una serie no la pone en stock: entra al confirmar una operación de entrada.';
export const SITUACION_LOTE: Readonly<Record<'CON_STOCK' | 'AGOTADO' | 'SIN_ENTRADAS', [string, string, string]>> = {
  CON_STOCK: ['Con stock', 'exito-suave', 'Tiene existencias en tus almacenes.'],
  AGOTADO: ['Agotado', 'neutra', 'Tuvo entradas pero hoy está a 0 en tus almacenes.'],
  SIN_ENTRADAS: ['Sin entradas', 'info', 'Creado, pero todavía no ha entrado stock con este lote (en tus almacenes).'],
};
export function EtiquetaSituacionLote({ s }: { s: keyof typeof SITUACION_LOTE | undefined }): ReactNode {
  if (s === undefined) return null;
  const [t, c, ayuda] = SITUACION_LOTE[s];
  return <span className={`etiqueta ${c}`} title={ayuda}>{t}</span>;
}
/** A partir de cuántos días se avisa de que un lote caduca pronto. */
export const DIAS_AVISO_CADUCIDAD = 30;

/** F13.2: «Bloqueado» (no sale, no se consume ni se traslada). Un lote activo no lleva etiqueta. */
export function EtiquetaEstadoLote({ estado }: { estado: EstadoLote | undefined }): ReactNode {
  if (estado !== 'BLOQUEADO') return null;
  return <span className="etiqueta error" title="No puede salir, consumirse ni trasladarse. Sí admite entradas, inventario y ajustes.">Bloqueado</span>;
}

/** F13.2: bloquear o desbloquear con motivo obligatorio. */
export function DialogoBloqueo({ lote, bloquear, cerrar, alHacer }: {
  lote: { id: string; codigo: string }; bloquear: boolean; cerrar: () => void; alHacer: () => void;
}): ReactNode {
  const [motivo, setMotivo] = useState('');
  const [error, setError] = useState<unknown>(null);
  const [enviando, setEnviando] = useState(false);
  async function hacer(): Promise<void> {
    setError(null);
    setEnviando(true);
    try {
      await api.post(`/api/inventario/lotes/${lote.id}/${bloquear ? 'bloquear' : 'desbloquear'}`, { motivo: motivo.trim() });
      alHacer();
    } catch (e) {
      setError(e);
    } finally {
      setEnviando(false);
    }
  }
  return (
    <Modal titulo={`${bloquear ? 'Bloquear' : 'Desbloquear'} el lote ${lote.codigo}`} cerrar={cerrar}
      pie={<><button type="button" onClick={cerrar}>Cancelar</button>
        <button type="button" className={bloquear ? 'peligro' : 'principal'} disabled={enviando || motivo.trim().length < 5} onClick={() => void hacer()}>
          {bloquear ? 'Bloquear lote' : 'Desbloquear lote'}</button></>}>
      {error !== null && <ErrorCaja error={error} />}
      <p className="pequena">{bloquear
        ? 'Un lote bloqueado no puede salir, consumirse ni trasladarse en ningún almacén. Sí admite entradas, inventario, ajustes y rectificaciones. Queda registrado quién y por qué.'
        : 'El lote volverá a poder salir, consumirse y trasladarse. Queda registrado quién y por qué.'}</p>
      <label className="campo"><span>Motivo (obligatorio)</span>
        <textarea rows={2} maxLength={500} value={motivo} onChange={(ev) => setMotivo(ev.target.value)} placeholder={bloquear ? 'Por ejemplo: retirada del proveedor, análisis pendiente' : 'Por ejemplo: análisis correcto'} /></label>
    </Modal>
  );
}

// ================================================================ caducidad

/** «Caducado» (rojo), «Caduca en N días» (aviso) o sólo la fecha. Nunca bloquea: informa. */
export function EtiquetaCaducidad({ fechaCaducidad, caducidad }: { fechaCaducidad: string | null; caducidad?: CaducidadLote | null; }): ReactNode {
  if (fechaCaducidad === null) return null;
  const dias = caducidad?.dias ?? null;
  const caducado = caducidad?.estado === 'CADUCADO';
  const pronto = !caducado && (caducidad?.situacion === 'PROXIMA' || (dias !== null && dias <= DIAS_AVISO_CADUCIDAD));
  return (
    <span className="caducidad">
      <span className="pequena">Cad. {fecha(fechaCaducidad)}</span>
      {caducado && <span className="etiqueta error" title={dias === null ? undefined : `Caducado hace ${String(-dias)} día(s)`}>Caducado</span>}
      {pronto && <span className="etiqueta aviso">Caduca en {dias} día(s)</span>}
    </span>
  );
}
export const caducidadDe = (fechaCaducidad: string | null, caducado: boolean): CaducidadLote =>
  ({ estado: fechaCaducidad === null ? null : caducado ? 'CADUCADO' : 'VIGENTE', dias: null });

// ============================================================ series: texto

/** Separa lo pegado o escaneado (una por línea; también comas, punto y coma o tabuladores). */
export function parsearSeries(texto: string): { numeros: string[]; duplicadas: string[]; largas: string[] } {
  const numeros = texto.split(/[\n\r,;\t]+/).map((x) => x.trim()).filter((x) => x !== '');
  const vistos = new Set<string>();
  const duplicadas = new Set<string>();
  for (const n of numeros) {
    const k = n.toUpperCase();
    if (vistos.has(k)) duplicadas.add(n);
    vistos.add(k);
  }
  return { numeros, duplicadas: [...duplicadas], largas: numeros.filter((n) => n.length > 80) };
}
/** Las series a enviar (sin repetidas). */
export const seriesUnicas = (texto: string): string[] => {
  const vistos = new Set<string>();
  return parsearSeries(texto).numeros.filter((n) => { const k = n.toUpperCase(); if (vistos.has(k)) return false; vistos.add(k); return true; });
};

/**
 * Editor de series para muchas unidades: UN área de texto (pegar o escanear,
 * una por línea), contador «N / M series registradas», repetidas y números
 * demasiado largos se señalan antes de enviar. Para sacar stock, además, un
 * buscador de las series que están en el almacén (se añaden al texto).
 */
export function EditorSeries({ id, etiqueta = 'Números de serie', valor, cambiar, esperadas, error, disponibles }: {
  id: string; etiqueta?: string; valor: string; cambiar: (v: string) => void; esperadas?: number | null; error?: string | undefined;
  /** Búsqueda de series disponibles (salidas, traslados, ajustes de salida). */
  disponibles?: ((texto: string) => Promise<OpcionSerie[]>) | undefined;
}): ReactNode {
  const { numeros, duplicadas, largas } = parsearSeries(valor);
  const n = numeros.length - duplicadas.length;
  const [elegida, setElegida] = useState<OpcionSerie | null>(null);
  const cuadra = esperadas === undefined || esperadas === null || esperadas === n;
  return (
    <div className="campo editor-series">
      <label htmlFor={id}>{etiqueta}</label>
      <textarea id={id} rows={Math.min(8, Math.max(3, numeros.length + 1))} spellCheck={false} autoCapitalize="characters" autoComplete="off"
        placeholder={'Una por línea (puedes pegar o escanear)\nSN001\nSN002'} value={valor} onChange={(ev) => cambiar(ev.target.value)}
        aria-describedby={`${id}-contador`} />
      <div id={`${id}-contador`} className={`pequena contador-series${cuadra ? '' : ' aviso-texto'}`} aria-live="polite">
        {esperadas === undefined || esperadas === null ? `${String(n)} serie(s)` : `${String(n)} / ${String(esperadas)} series registradas`}
      </div>
      {duplicadas.length > 0 && <div className="error-campo">Repetidas: {duplicadas.join(', ')}</div>}
      {largas.length > 0 && <div className="error-campo">Demasiado largas (máx. 80): {largas.join(', ')}</div>}
      {error !== undefined && <div className="error-campo">{error}</div>}
      {disponibles !== undefined && (
        <Buscador<OpcionSerie> etiqueta="Añadir de las disponibles" valor={elegida} placeholder="Busca una serie en stock aquí…"
          buscar={disponibles} clave={(s) => s.id} texto={(s) => s.numero} detalle={(s) => (s.lote === null ? null : `Lote ${s.lote}`)}
          sinResultados="No hay series disponibles con ese número en este almacén."
          alElegir={(s) => {
            setElegida(null);
            if (s !== null && !numeros.some((x) => x.toUpperCase() === s.numero.toUpperCase())) cambiar(valor.trim() === '' ? s.numero : `${valor.replace(/\s+$/, '')}\n${s.numero}`);
          }} />
      )}
    </div>
  );
}

export const buscarSeries = (q: { idArticulo: string; idAlmacen?: string | undefined; enStock?: boolean; idLote?: string | null | undefined }) => async (texto: string): Promise<OpcionSerie[]> => {
  const p = new URLSearchParams({ idArticulo: q.idArticulo, texto, limite: '20' });
  if (q.idAlmacen !== undefined) p.set('idAlmacen', q.idAlmacen);
  if (q.enStock !== undefined) p.set('enStock', String(q.enStock));
  if (q.idLote != null) p.set('idLote', q.idLote);
  return (await api.get<{ filas: OpcionSerie[] }>(`/api/inventario/opciones/series?${p.toString()}`)).filas;
};

// ===================================================================== lote

export const buscarLotes = (q: { idArticulo: string; idAlmacen?: string | undefined; conStock?: boolean }) => async (texto: string): Promise<OpcionLote[]> => {
  const p = new URLSearchParams({ idArticulo: q.idArticulo, texto, limite: '20' });
  if (q.idAlmacen !== undefined) p.set('idAlmacen', q.idAlmacen);
  if (q.conStock === true) p.set('conStock', 'true');
  return (await api.get<{ filas: OpcionLote[] }>(`/api/inventario/opciones/lotes?${p.toString()}`)).filas;
};

/** Alta de lote (modal). Con `articulo` fijo se usa en línea desde una operación o un inventario. */
export function NuevoLote({ articulo, cerrar, alCrear }: {
  articulo?: { id: string; codigo: string; descripcion: string; controlaCaducidad?: boolean | undefined } | undefined;
  cerrar: () => void; alCrear: (l: LoteDetalle) => void;
}): ReactNode {
  const [art, setArt] = useState<OpcionArticuloStock | null>(null);
  const [v, setV] = useState({ codigo: '', fechaFabricacion: '', fechaCaducidad: '', observaciones: '', proveedor: '' });
  const [error, setError] = useState<unknown>(null);
  const [enviando, setEnviando] = useState(false);
  const campos = erroresDe(error);
  const idArticulo = articulo?.id ?? art?.id ?? null;
  const controlaCaducidad = articulo?.controlaCaducidad ?? art?.trazabilidad?.controlaCaducidad ?? false;
  async function crear(): Promise<void> {
    setError(null);
    setEnviando(true);
    try {
      alCrear(await api.post<LoteDetalle>('/api/inventario/lotes', {
        idArticulo, codigo: v.codigo, fechaFabricacion: v.fechaFabricacion || null, fechaCaducidad: v.fechaCaducidad || null, observaciones: v.observaciones || null,
        proveedor: v.proveedor.trim() || null,
      }));
    } catch (e) {
      setError(e);
    } finally {
      setEnviando(false);
    }
  }
  return (
    <Modal titulo="Nuevo lote" cerrar={cerrar}
      //  type="button": este modal se abre DENTRO del formulario de la operación; no debe enviarlo.
      pie={<><button type="button" onClick={cerrar}>Cancelar</button>
        <button type="button" className="principal" disabled={enviando || idArticulo === null || v.codigo.trim() === ''} onClick={() => void crear()}>Crear lote</button></>}>
      {/* Intro en un campo no envía el formulario de la operación que contiene este modal. */}
      <div onKeyDown={(ev) => { if (ev.key === 'Enter' && (ev.target as HTMLElement).tagName === 'INPUT') ev.preventDefault(); }}>
      <p className="nota info pequena" role="note">{TEXTO_LOTE_SIN_STOCK}</p>
      {error !== null && Object.keys(campos).length === 0 && <ErrorCaja error={error} />}
      {articulo !== undefined
        ? <p>Artículo: <strong>{nombreArticulo(articulo)}</strong></p>
        : (
          <Buscador<OpcionArticuloStock> etiqueta="Artículo" valor={art} alElegir={setArt} clave={(a) => a.id} texto={nombreArticulo}
            buscar={async (t) => (await api.get<{ filas: OpcionArticuloStock[] }>(`/api/inventario/opciones/articulos?${new URLSearchParams({ texto: t, limite: '50' }).toString()}`)).filas
              .filter((a) => a.trazabilidad?.controlaLotes === true)}
            sinResultados="Ningún artículo activo controla lotes." error={campos['idArticulo']} />
        )}
      <div className="form-rejilla">
        <div className="campo"><label htmlFor="nl-codigo">Código de lote (del fabricante)</label>
          <input id="nl-codigo" maxLength={60} autoComplete="off" value={v.codigo} onChange={(ev) => setV({ ...v, codigo: ev.target.value })} />
          {campos['codigo'] !== undefined && <div className="error-campo">{campos['codigo']}</div>}</div>
        <div className="campo"><label htmlFor="nl-fab">Fecha de fabricación</label>
          <input id="nl-fab" type="date" value={v.fechaFabricacion} onChange={(ev) => setV({ ...v, fechaFabricacion: ev.target.value })} />
          {campos['fechaFabricacion'] !== undefined && <div className="error-campo">{campos['fechaFabricacion']}</div>}</div>
        <div className="campo"><label htmlFor="nl-cad">Fecha de caducidad{controlaCaducidad ? ' (obligatoria)' : ''}</label>
          <input id="nl-cad" type="date" value={v.fechaCaducidad} onChange={(ev) => setV({ ...v, fechaCaducidad: ev.target.value })} />
          {campos['fechaCaducidad'] !== undefined && <div className="error-campo">{campos['fechaCaducidad']}</div>}</div>
      </div>
      <div className="campo"><label htmlFor="nl-prov">Proveedor (opcional)</label>
        <input id="nl-prov" maxLength={120} autoComplete="off" value={v.proveedor} onChange={(ev) => setV({ ...v, proveedor: ev.target.value })} />
        {campos['proveedor'] !== undefined && <div className="error-campo">{campos['proveedor']}</div>}</div>
      <div className="campo"><label htmlFor="nl-obs">Observaciones</label>
        <textarea id="nl-obs" rows={2} maxLength={1000} value={v.observaciones} onChange={(ev) => setV({ ...v, observaciones: ev.target.value })} /></div>
      </div>
    </Modal>
  );
}

/**
 * Selector de lote de un artículo. Maqueta (FASE 9.4-B): el campo y «Nuevo lote» comparten
 * fila y se alinean por la base del control; avisos y ayudas van debajo, sin desplazar los
 * campos vecinos. En una salida sólo se ofrecen lotes con stock en el almacén (y no hay alta).
 */
export function SelectorLote({ articulo, idAlmacen, conStock = false, valor, alElegir, error, etiqueta = 'Lote' }: {
  articulo: { id: string; codigo: string; descripcion: string; controlaCaducidad?: boolean | undefined };
  idAlmacen?: string | undefined; conStock?: boolean; valor: OpcionLote | null; alElegir: (l: OpcionLote | null) => void;
  error?: string | undefined; etiqueta?: string;
}): ReactNode {
  const { puede } = useSesion();
  const [nuevo, setNuevo] = useState(false);
  const crea = !conStock && puede('lotes:crear');
  return (
    <div className="selector-lote">
      <div className={`campo-con-accion${crea ? '' : ' sin-accion'}`}>
        <Buscador<OpcionLote> etiqueta={etiqueta} valor={valor} alElegir={alElegir} contexto={`${articulo.id}-${idAlmacen ?? ''}-${String(conStock)}`}
          buscar={buscarLotes({ idArticulo: articulo.id, idAlmacen, conStock })} clave={(l) => l.id} texto={(l) => l.codigo}
          detalle={(l) => <><EtiquetaEstadoLote estado={l.estado} />{l.fechaCaducidad !== null && <EtiquetaCaducidad fechaCaducidad={l.fechaCaducidad} caducidad={l.caducidad} />}
            <span className="pequena"> · {l.saldo === null || Number(l.saldo) === 0 ? 'sin stock aquí' : `${l.saldo} en stock aquí`}</span></>}
          placeholder="Código de lote…" sinResultados={conStock ? 'Ningún lote con stock en este almacén.' : 'Ningún lote activo de este artículo. Créalo con «Nuevo lote».'} error={error} />
        {/* La acción va en su propia «celda de campo» con una etiqueta espaciadora (misma clase que las
            etiquetas reales): así queda a la altura del control aunque el campo muestre un error. */}
        {crea && (
          <div className="campo campo-accion">
            <label aria-hidden="true">&nbsp;</label>
            <button type="button" className="boton-accion-campo" onClick={() => setNuevo(true)}>Nuevo lote</button>
          </div>
        )}
      </div>
      {valor !== null && valor.estado === 'BLOQUEADO' && (
        <p className="nota error pequena" role="status">Lote bloqueado: no puede salir, consumirse ni trasladarse (sí entrar o ajustarse).</p>
      )}
      {valor !== null && valor.caducidad.estado === 'CADUCADO' && (
        <p className="nota aviso pequena" role="status">Lote caducado: se permite registrarlo, pero revisa antes de usarlo.</p>
      )}
      {nuevo && (
        <NuevoLote articulo={articulo} cerrar={() => setNuevo(false)}
          alCrear={(l) => { setNuevo(false); alElegir({ id: l.id, codigo: l.codigo, fechaCaducidad: l.fechaCaducidad, caducidad: l.caducidad, saldo: null, activo: l.activo, estado: l.estado ?? 'ACTIVO' }); }} />
      )}
    </div>
  );
}

// ========================================================= detalle de stock

export function DetalleStockModal({ idArticulo, idAlmacen, cerrar }: { idArticulo: string; idAlmacen: string; cerrar: () => void }): ReactNode {
  const d = useDatos(() => api.get<DetalleStock>(`/api/inventario/stock/detalle?${new URLSearchParams({ idArticulo, idAlmacen }).toString()}`), [idArticulo, idAlmacen]);
  return (
    <Modal titulo="Detalle del stock" cerrar={cerrar}>
      <Panel estado={d}>
        {(x) => {
          const agotados = x.lotesAgotados ?? [];
          const sinEntradas = x.lotesSinEntradas ?? 0;
          return (
          <>
            <div className="detalle-stock-cabecera">
              <div><strong>{nombreArticulo(x.articulo)}</strong><div className="suave pequena">{codigoAlmacen(x.almacen)} · {x.almacen.descripcion}</div></div>
              <div className="detalle-stock-total"><span className="suave pequena">Saldo total</span><strong>{x.saldo} {x.articulo.unidad}</strong></div>
            </div>
            {!x.articulo.controlaLotes && !x.articulo.controlaSeries && <p className="suave">Este artículo no controla lotes ni series: su stock es sólo el saldo total.</p>}
            {x.articulo.controlaLotes && (
              <p className="suave pequena">El saldo total es la suma de sus lotes. {TEXTO_LOTE_SIN_STOCK}</p>
            )}
            {x.articulo.controlaSeries && !x.articulo.controlaLotes && (
              <p className="suave pequena">Cada serie es una unidad: el saldo total es el número de series en stock aquí.</p>
            )}
            {x.articulo.controlaLotes && (x.lotes.length === 0 && agotados.length === 0 ? <p className="suave">Ningún lote ha tenido stock en este almacén.</p> : (
              <div className="tabla-desliza">
                <table aria-label="Stock por lote" className="tabla-tarjetas">
                  <thead><tr><th>Lote</th><th>Caducidad</th><th>Situación</th><th className="num">Saldo</th>{x.articulo.controlaSeries && <th>Series</th>}</tr></thead>
                  <tbody>
                    {x.lotes.map((l) => (
                      <tr key={l.id}>
                        <td className="celda-titulo mono"><Link to={`/inventario/lotes/${l.id}`}>{l.codigo}</Link></td>
                        <td data-label="Caducidad">{l.fechaCaducidad === null ? '—' : <EtiquetaCaducidad fechaCaducidad={l.fechaCaducidad} caducidad={l.caducidad} />}</td>
                        <td data-label="Situación"><EtiquetaSituacionLote s="CON_STOCK" /></td>
                        <td className="num" data-label="Saldo">{l.saldo} {x.articulo.unidad}</td>
                        {x.articulo.controlaSeries && <td data-label="Series" className="pequena mono">{l.series.map((s) => s.numero).join(', ') || '—'}</td>}
                      </tr>
                    ))}
                    {agotados.map((l) => (
                      <tr key={l.id} className="fila-agotada">
                        <td className="celda-titulo mono"><Link to={`/inventario/lotes/${l.id}`}>{l.codigo}</Link></td>
                        <td data-label="Caducidad">{l.fechaCaducidad === null ? '—' : <EtiquetaCaducidad fechaCaducidad={l.fechaCaducidad} caducidad={l.caducidad} />}</td>
                        <td data-label="Situación"><EtiquetaSituacionLote s="AGOTADO" /></td>
                        <td className="num" data-label="Saldo">0 {x.articulo.unidad}</td>
                        {x.articulo.controlaSeries && <td data-label="Series">—</td>}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}
            {x.articulo.controlaLotes && sinEntradas > 0 && (
              <p className="suave pequena">{sinEntradas} lote(s) del artículo creados sin entradas todavía: <Link to={`/inventario/articulos/${x.articulo.id}?tab=lotes`}>ver los lotes del artículo</Link>.</p>
            )}
            {x.articulo.controlaSeries && !x.articulo.controlaLotes && (x.series.length === 0 ? <p className="suave">Ninguna serie en stock aquí.</p> : (
              <>
                <p className="pequena">{x.series.length}{x.seriesTruncadas ? '+' : ''} serie(s) en stock:</p>
                <ul className="lista-series" aria-label="Series en stock">
                  {x.series.map((s) => <li key={s.id} className="mono"><Link to={`/inventario/series/${s.id}`}>{s.numero}</Link></li>)}
                </ul>
                {x.seriesTruncadas && <p className="suave pequena">Se muestran las primeras 1000. Consulta el resto en Stock → Por serie.</p>}
              </>
            ))}
            <p className="pequena detalle-stock-pie"><Link to={`/inventario/articulos/${x.articulo.id}?tab=movimientos`}>Ver el histórico de movimientos del artículo</Link></p>
          </>
          );
        }}
      </Panel>
    </Modal>
  );
}

// ================================================================== listados

function SituacionSerie({ s }: { s: SerieResumen }): ReactNode {
  if (s.estado.situacion === 'EN_STOCK') return <span className="etiqueta exito-suave">En stock · {s.estado.almacen === null ? '' : codigoAlmacen(s.estado.almacen)}</span>;
  if (s.estado.situacion === 'FUERA') return <span className="etiqueta neutra">Fuera de stock</span>;
  return <span className="etiqueta oscura" title="Está en stock en un almacén fuera de tu ámbito">Sin información en tu ámbito</span>;
}
const Activo = ({ v }: { v: boolean }): ReactNode => (v ? null : <Estado d={estadoArticulo(false)} />);

/**
 * Listado de lotes: en Stock → «Por lote» (todos) o en la ficha del artículo (`idArticulo`;
 * con `articulo` ofrece además «Nuevo lote» para ese artículo).
 */
export function ListaLotes({ idArticulo, articulo }: {
  idArticulo?: string; articulo?: { id: string; codigo: string; descripcion: string; controlaCaducidad?: boolean | undefined };
}): ReactNode {
  const { puede } = useSesion();
  const navegar = useNavigate();
  const [nuevo, setNuevo] = useState(false);
  const [q, setQ] = useState({ texto: '', stock: 'TODOS', caducados: false, caducaAntes: '', activo: 'true', estado: '' });
  const [pagina, setPagina] = useState(1);
  const [elegidos, setElegidos] = useState<Record<string, ElementoEtiqueta>>({});
  const [imprimir, setImprimir] = useState<ElementoEtiqueta[] | null>(null);
  const [bloqueo, setBloqueo] = useState<{ lote: LoteResumen; bloquear: boolean } | null>(null);
  const etiquetas = puede('lotes:etiquetas');
  const datos = useDatos(() => {
    const p = new URLSearchParams({ pagina: String(pagina), porPagina: String(POR_PAGINA), stock: q.stock, activo: q.activo });
    if (q.estado !== '') p.set('estado', q.estado);
    if (q.texto !== '') p.set('texto', q.texto);
    if (q.caducados) p.set('caducados', 'true');
    if (q.caducaAntes !== '') p.set('caducaAntes', q.caducaAntes);
    if (idArticulo !== undefined) p.set('idArticulo', idArticulo);
    return api.get<{ filas: LoteResumen[]; total: number }>(`/api/inventario/lotes?${p.toString()}`);
  }, [q.texto, q.stock, q.caducados, q.caducaAntes, q.activo, q.estado, idArticulo, pagina]);
  const elemento = (l: LoteResumen): ElementoEtiqueta => ({ tipo: 'LOT', id: l.id, titulo: `${l.codigo} · ${l.articulo.codigo}` });
  const nElegidos = Object.keys(elegidos).length;
  const fijar = <K extends keyof typeof q>(k: K, v: (typeof q)[K]): void => { setQ((a) => ({ ...a, [k]: v })); setPagina(1); };
  return (
    <section className="panel" aria-label="Lotes">
      <div className="panel-cuerpo">
        {articulo !== undefined && (
          <div className="explicacion-traza">
            <p className="suave pequena">Este artículo se controla por lotes: cada entrada y salida indica su lote. {TEXTO_LOTE_SIN_STOCK}</p>
            {puede('lotes:crear') && <button type="button" onClick={() => setNuevo(true)}>Nuevo lote</button>}
          </div>
        )}
        {nuevo && articulo !== undefined && <NuevoLote articulo={articulo} cerrar={() => setNuevo(false)} alCrear={(l) => navegar(`/inventario/lotes/${l.id}`)} />}
        <FiltrosResponsive contador={[q.stock !== 'TODOS', q.caducados, q.caducaAntes !== '', q.activo !== 'true', q.estado !== ''].filter(Boolean).length + (q.texto !== '' ? 1 : 0)}>
          <div className="filtros-rejilla">
            <div className="campo"><label htmlFor="lo-texto">Buscar</label>
              <input id="lo-texto" type="search" placeholder={idArticulo === undefined ? 'Lote, artículo o proveedor' : 'Código de lote o proveedor'} value={q.texto} onChange={(ev) => fijar('texto', ev.target.value)} /></div>
            <label className="campo"><span>Stock</span>
              <select value={q.stock} onChange={(ev) => fijar('stock', ev.target.value)}>
                <option value="TODOS">Todos</option><option value="CON_STOCK">Con stock</option><option value="AGOTADOS">Sin stock (agotados o sin entradas)</option>
              </select>
            </label>
            <label className="campo"><span>Caduca antes de</span><input type="date" value={q.caducaAntes} onChange={(ev) => fijar('caducaAntes', ev.target.value)} /></label>
            <label className="campo"><span>Estado</span>
              <select value={q.activo} onChange={(ev) => fijar('activo', ev.target.value)}>
                <option value="true">Activos</option><option value="false">Inactivos</option><option value="TODOS">Todos</option>
              </select>
            </label>
            <label className="campo"><span>Bloqueo</span>
              <select value={q.estado} onChange={(ev) => fijar('estado', ev.target.value)} aria-label="Bloqueo">
                <option value="">Todos</option><option value="ACTIVO">No bloqueados</option><option value="BLOQUEADO">Bloqueados</option>
              </select>
            </label>
            <label className="casilla"><input type="checkbox" checked={q.caducados} onChange={(ev) => fijar('caducados', ev.target.checked)} /> Sólo caducados</label>
          </div>
        </FiltrosResponsive>
        {etiquetas && nElegidos > 0 && (
          <div className="fila" role="status" style={{ gap: 8, margin: '8px 0', flexWrap: 'wrap' }}>
            <span className="pequena">{nElegidos} lote(s) elegido(s)</span>
            <button type="button" className="principal" onClick={() => setImprimir(Object.values(elegidos))}>Imprimir etiquetas</button>
            <button type="button" onClick={() => setElegidos({})}>Quitar selección</button>
          </div>
        )}
        <Panel estado={datos}>
          {(d) => (d.filas.length === 0 ? <Vacio titulo="Sin lotes">{idArticulo !== undefined ? 'Los lotes se dan de alta desde una entrada (Movimientos) o con «Nuevo lote».' : null}</Vacio> : (
            <>
              <div className="tabla-desliza">
                <table aria-label="Lotes" className="tabla-tarjetas">
                  <thead><tr>{etiquetas && <th aria-label="Elegir" />}<th>Lote</th>{idArticulo === undefined && <th>Artículo</th>}<th>Fabricación</th><th>Caducidad</th><th>Recepción</th><th>Proveedor</th><th>Situación</th><th className="num">Stock</th><th className="num">Almacenes</th>
                    {(etiquetas || puede('lotes:bloquear')) && <th aria-label="Acciones" />}</tr></thead>
                  <tbody>
                    {d.filas.map((l) => (
                      <tr key={l.id}>
                        {etiquetas && <td data-label="Elegir"><input type="checkbox" aria-label={`Elegir el lote ${l.codigo}`} checked={elegidos[l.id] !== undefined}
                          onChange={(ev) => { const n = { ...elegidos }; if (ev.target.checked) n[l.id] = elemento(l); else delete n[l.id]; setElegidos(n); }} /></td>}
                        <td className="celda-titulo mono"><Link to={`/inventario/lotes/${l.id}`}>{l.codigo}</Link> <Activo v={l.activo} /> <EtiquetaEstadoLote estado={l.estado} /></td>
                        {idArticulo === undefined && <td data-label="Artículo">{nombreArticulo(l.articulo)}</td>}
                        <td data-label="Fabricación">{fecha(l.fechaFabricacion)}</td>
                        <td data-label="Caducidad">{l.fechaCaducidad === null ? '—' : <EtiquetaCaducidad fechaCaducidad={l.fechaCaducidad} caducidad={l.caducidad} />}</td>
                        <td data-label="Recepción">{l.fechaRecepcion == null ? '—' : fecha(l.fechaRecepcion)}</td>
                        <td data-label="Proveedor">{l.proveedor ?? '—'}</td>
                        <td data-label="Situación"><EtiquetaSituacionLote s={l.situacion} /></td>
                        <td className="num" data-label="Stock">{l.stock} {l.articulo.unidad}</td>
                        <td className="num" data-label="Almacenes">{l.nAlmacenes}</td>
                        {(etiquetas || puede('lotes:bloquear')) && (
                          <td data-label="Acciones"><div className="fila" style={{ gap: 6, flexWrap: 'wrap' }}>
                            {etiquetas && <button type="button" onClick={() => setImprimir([elemento(l)])} aria-label={`Etiqueta del lote ${l.codigo}`}>Etiqueta</button>}
                            {puede('lotes:bloquear') && <button type="button" onClick={() => setBloqueo({ lote: l, bloquear: l.estado !== 'BLOQUEADO' })}
                              aria-label={`${l.estado === 'BLOQUEADO' ? 'Desbloquear' : 'Bloquear'} el lote ${l.codigo}`}>{l.estado === 'BLOQUEADO' ? 'Desbloquear' : 'Bloquear'}</button>}
                          </div></td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <Paginacion pagina={pagina} hayMas={pagina * POR_PAGINA < d.total} fijar={setPagina} total={d.total} porPagina={POR_PAGINA} />
            </>
          ))}
        </Panel>
      </div>
      {imprimir !== null && <DialogoEtiquetas elementos={imprimir} cerrar={() => setImprimir(null)} />}
      {bloqueo !== null && <DialogoBloqueo lote={bloqueo.lote} bloquear={bloqueo.bloquear} cerrar={() => setBloqueo(null)} alHacer={() => { setBloqueo(null); datos.recargar(); }} />}
    </section>
  );
}

/** Listado de series; con `idArticulo` / `idLote` es la pestaña de una ficha. */
export function ListaSeries({ idArticulo, idLote }: { idArticulo?: string; idLote?: string }): ReactNode {
  const [q, setQ] = useState({ texto: '', enStock: '', activo: 'true' });
  const [pagina, setPagina] = useState(1);
  const datos = useDatos(() => {
    const p = new URLSearchParams({ pagina: String(pagina), porPagina: String(POR_PAGINA), activo: q.activo });
    if (q.texto !== '') p.set('texto', q.texto);
    if (q.enStock !== '') p.set('enStock', q.enStock);
    if (idArticulo !== undefined) p.set('idArticulo', idArticulo);
    if (idLote !== undefined) p.set('idLote', idLote);
    return api.get<{ filas: SerieResumen[]; total: number }>(`/api/inventario/series?${p.toString()}`);
  }, [q.texto, q.enStock, q.activo, idArticulo, idLote, pagina]);
  const fijar = (k: keyof typeof q, v: string): void => { setQ((a) => ({ ...a, [k]: v })); setPagina(1); };
  const fijo = idArticulo !== undefined || idLote !== undefined;
  return (
    <section className="panel" aria-label="Series">
      <div className="panel-cuerpo">
        <FiltrosResponsive contador={[q.enStock !== '', q.activo !== 'true'].filter(Boolean).length + (q.texto !== '' ? 1 : 0)}>
          <div className="filtros-rejilla">
            <div className="campo"><label htmlFor="se-texto">Buscar</label>
              <input id="se-texto" type="search" placeholder={fijo ? 'Número de serie' : 'Número, lote o artículo'} value={q.texto} onChange={(ev) => fijar('texto', ev.target.value)} /></div>
            <label className="campo"><span>Situación</span>
              <select value={q.enStock} onChange={(ev) => fijar('enStock', ev.target.value)}>
                <option value="">Todas</option><option value="true">En stock</option><option value="false">Fuera de stock</option>
              </select>
            </label>
            <label className="campo"><span>Estado</span>
              <select value={q.activo} onChange={(ev) => fijar('activo', ev.target.value)}>
                <option value="true">Activas</option><option value="false">Inactivas</option><option value="TODOS">Todas</option>
              </select>
            </label>
          </div>
        </FiltrosResponsive>
        <Panel estado={datos}>
          {(d) => (d.filas.length === 0 ? <Vacio titulo="Sin series" /> : (
            <>
              <div className="tabla-desliza">
                <table aria-label="Series" className="tabla-tarjetas">
                  <thead><tr><th>Número de serie</th>{idArticulo === undefined && <th>Artículo</th>}{idLote === undefined && <th>Lote</th>}<th>Situación</th><th>Último movimiento</th></tr></thead>
                  <tbody>
                    {d.filas.map((s) => (
                      <tr key={s.id}>
                        <td className="celda-titulo mono"><Link to={`/inventario/series/${s.id}`}>{s.numero}</Link> <Activo v={s.activo} /></td>
                        {idArticulo === undefined && <td data-label="Artículo">{nombreArticulo(s.articulo)}</td>}
                        {idLote === undefined && <td data-label="Lote">{s.lote === null ? '—' : <><Link className="mono" to={`/inventario/lotes/${s.lote.id}`}>{s.lote.codigo}</Link> <EtiquetaCaducidad fechaCaducidad={s.lote.fechaCaducidad} caducidad={s.lote.caducidad} /></>}</td>}
                        <td data-label="Situación"><SituacionSerie s={s} /></td>
                        <td className="pequena" data-label="Último movimiento">{fechaHora(s.ultimoMovimiento)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <Paginacion pagina={pagina} hayMas={pagina * POR_PAGINA < d.total} fijar={setPagina} total={d.total} porPagina={POR_PAGINA} />
            </>
          ))}
        </Panel>
      </div>
    </section>
  );
}

// ============================================================ alta de serie

export function NuevaSerie({ cerrar }: { cerrar: () => void }): ReactNode {
  const navegar = useNavigate();
  const [art, setArt] = useState<OpcionArticuloStock | null>(null);
  const [lote, setLote] = useState<OpcionLote | null>(null);
  const [numero, setNumero] = useState('');
  const [error, setError] = useState<unknown>(null);
  const campos = erroresDe(error);
  async function crear(): Promise<void> {
    setError(null);
    try {
      const s = await api.post<SerieDetalle>('/api/inventario/series', { idArticulo: art?.id ?? null, numero, idLote: lote?.id ?? null });
      navegar(`/inventario/series/${s.id}`);
    } catch (e) {
      setError(e);
    }
  }
  return (
    <Modal titulo="Nueva serie" cerrar={cerrar}
      pie={<><button onClick={cerrar}>Cancelar</button><button className="principal" disabled={art === null || numero.trim() === ''} onClick={() => void crear()}>Crear serie</button></>}>
      {error !== null && Object.keys(campos).length === 0 && <ErrorCaja error={error} />}
      <Buscador<OpcionArticuloStock> etiqueta="Artículo" valor={art} alElegir={(a) => { setArt(a); setLote(null); }} clave={(a) => a.id} texto={nombreArticulo}
        buscar={async (t) => (await api.get<{ filas: OpcionArticuloStock[] }>(`/api/inventario/opciones/articulos?${new URLSearchParams({ texto: t, limite: '50' }).toString()}`)).filas
          .filter((a) => a.trazabilidad?.controlaSeries === true)}
        sinResultados="Ningún artículo activo controla números de serie." error={campos['idArticulo']} />
      {art?.trazabilidad?.controlaLotes === true && <SelectorLote articulo={{ ...art, controlaCaducidad: art.trazabilidad.controlaCaducidad }} valor={lote} alElegir={setLote} error={campos['idLote']} />}
      <div className="campo"><label htmlFor="ns-num">Número de serie</label>
        <input id="ns-num" maxLength={80} autoComplete="off" value={numero} onChange={(ev) => setNumero(ev.target.value)} />
        {campos['numero'] !== undefined && <div className="error-campo">{campos['numero']}</div>}</div>
      <p className="nota info pequena" role="note">{TEXTO_SERIE_SIN_STOCK} Lo habitual es teclearlas o escanearlas directamente en la entrada.</p>
    </Modal>
  );
}

// =================================================================== fichas

function Dato({ e, v }: { e: string; v: ReactNode }): ReactNode {
  return <div className="dato"><div className="etiqueta-dato">{e}</div><div className="valor-dato">{v === null || v === '' ? '—' : v}</div></div>;
}

const TEXTO_EVENTO: Readonly<Record<string, string>> = {
  ALTA: 'Alta', EDICION: 'Datos modificados', ESTADO: 'Activación cambiada', BLOQUEO: 'Bloqueado', DESBLOQUEO: 'Desbloqueado', IDENTIFICADOR: 'Código del lote',
};
function Cambios({ ruta }: { ruta: string }): ReactNode {
  const h = useHistorial<EventoTrazabilidad>(ruta);
  return (
    <div className="panel"><div className="panel-cuerpo">
      <Panel estado={h}>{(x) => (
        <ol className="lista-historial">
          {x.filas.map((e) => (
            <li key={e.id}><strong>{TEXTO_EVENTO[e.tipo] ?? e.tipo}</strong>
              {e.cambios !== null && <CambiosEvento cambios={e.cambios} />}
              <div className="suave pequena">{e.usuario.nombre} · {fechaHora(e.ocurridoEn)}</div></li>
          ))}
        </ol>
      )}</Panel>
      <PieHistorial historial={h} />
    </div></div>
  );
}

function EditarLote({ l, cerrar, alGuardar }: { l: LoteDetalle; cerrar: () => void; alGuardar: () => void }): ReactNode {
  const [v, setV] = useState({ codigo: l.codigo, fechaFabricacion: l.fechaFabricacion ?? '', fechaCaducidad: l.fechaCaducidad ?? '', observaciones: l.observaciones ?? '', proveedor: l.proveedor ?? '' });
  const [error, setError] = useState<unknown>(null);
  const campos = erroresDe(error);
  async function guardar(): Promise<void> {
    setError(null);
    try {
      await api.patch(`/api/inventario/lotes/${l.id}`, {
        ...(v.codigo !== l.codigo ? { codigo: v.codigo } : {}), fechaFabricacion: v.fechaFabricacion || null, fechaCaducidad: v.fechaCaducidad || null,
        observaciones: v.observaciones || null, proveedor: v.proveedor.trim() || null, versionEsperada: l.actualizadoEn,
      });
      alGuardar();
    } catch (e) {
      setError(e);
    }
  }
  return (
    <Modal titulo={`Editar lote ${l.codigo}`} cerrar={cerrar} pie={<><button onClick={cerrar}>Cancelar</button><button className="principal" onClick={() => void guardar()}>Guardar</button></>}>
      {error !== null && Object.keys(campos).length === 0 && <ErrorCaja error={error} />}
      <div className="campo"><label htmlFor="el-codigo">Código de lote</label>
        <input id="el-codigo" maxLength={60} value={v.codigo} disabled={l.usado} onChange={(ev) => setV({ ...v, codigo: ev.target.value })} />
        {l.usado && <div className="suave pequena">Ya tiene movimientos: su código no cambia.</div>}
        {campos['codigo'] !== undefined && <div className="error-campo">{campos['codigo']}</div>}</div>
      <div className="form-rejilla">
        <div className="campo"><label htmlFor="el-fab">Fabricación</label><input id="el-fab" type="date" value={v.fechaFabricacion} onChange={(ev) => setV({ ...v, fechaFabricacion: ev.target.value })} /></div>
        <div className="campo"><label htmlFor="el-cad">Caducidad</label><input id="el-cad" type="date" value={v.fechaCaducidad} onChange={(ev) => setV({ ...v, fechaCaducidad: ev.target.value })} />
          {campos['fechaCaducidad'] !== undefined && <div className="error-campo">{campos['fechaCaducidad']}</div>}</div>
      </div>
      <div className="campo"><label htmlFor="el-prov">Proveedor</label><input id="el-prov" maxLength={120} value={v.proveedor} onChange={(ev) => setV({ ...v, proveedor: ev.target.value })} />
        {campos['proveedor'] !== undefined && <div className="error-campo">{campos['proveedor']}</div>}</div>
      <div className="campo"><label htmlFor="el-obs">Observaciones</label><textarea id="el-obs" rows={2} maxLength={1000} value={v.observaciones} onChange={(ev) => setV({ ...v, observaciones: ev.target.value })} /></div>
    </Modal>
  );
}

export function PaginaLote(): ReactNode {
  const { id = '' } = useParams();
  const { puede } = useSesion();
  const [params, setParams] = useSearchParams();
  const tab = params.get('tab') ?? 'info';
  const lote = useDatos(() => api.get<LoteDetalle>(`/api/inventario/lotes/${encodeURIComponent(id)}`), [id]);
  const [editar, setEditar] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [bloqueo, setBloqueo] = useState<boolean | null>(null);
  const [imprimir, setImprimir] = useState(false);
  const l = lote.datos;
  const migas = [{ texto: 'Inventario' }, { texto: 'Stock', a: '/inventario/stock' }, { texto: 'Por lote', a: '/inventario/stock?tab=lotes' }, { texto: l?.codigo ?? '…' }];
  if (lote.error instanceof FalloApi && (lote.error.estado === 404 || lote.error.estado === 403)) {
    return <Shell migas={migas} titulo="Lote no disponible"><div className="panel"><Vacio titulo="Este lote no existe o no tienes acceso"><Link to="/inventario/stock?tab=lotes">Volver</Link></Vacio></div></Shell>;
  }
  async function activar(activo: boolean): Promise<void> {
    if (l === null) return;
    setError(null);
    try {
      await api.patch(`/api/inventario/lotes/${l.id}`, { activo, versionEsperada: l.actualizadoEn });
      lote.recargar();
    } catch (e) {
      setError(e);
    }
  }
  const pestanas: [string, string][] = [['info', 'Información'], ...(l?.articulo.controlaSeries === true && puede('series:leer') ? [['series', 'Series'] as [string, string]] : []),
    ...(puede('movimientos_stock:leer') ? [['ledger', 'Histórico'] as [string, string]] : []),
    ...(puede('lotes:etiquetas') ? [['etiqueta', 'Etiqueta'] as [string, string]] : []), ['historial', 'Cambios']];
  const bloqueado = l?.estado === 'BLOQUEADO';
  const acciones: ReactNode[] = [];
  if (l !== null && puede('lotes:etiquetas')) acciones.push(<button key="e" onClick={() => setImprimir(true)}>Imprimir etiqueta</button>);
  if (l !== null && puede('lotes:bloquear')) acciones.push(<button key="b" className={bloqueado ? undefined : 'peligro'} onClick={() => setBloqueo(!bloqueado)}>{bloqueado ? 'Desbloquear' : 'Bloquear'}</button>);
  if (l !== null && puede('lotes:gestionar')) {
    acciones.push(<button key="ed" onClick={() => setEditar(true)}>Editar</button>);
    acciones.push(<button key="ac" onClick={() => void activar(!l.activo)}>{l.activo ? 'Desactivar' : 'Reactivar'}</button>);
  }
  return (
    <Shell dominio="articulos" migas={migas} titulo={l === null ? 'Lote' : `Lote ${l.codigo}`}
      subtitulo={l === null ? undefined : <span className="fila" style={{ flexWrap: 'wrap' }}><span>{nombreArticulo(l.articulo)}</span><EtiquetaEstadoLote estado={l.estado} /><EtiquetaSituacionLote s={l.situacion} /><Activo v={l.activo} />
        {l.fechaCaducidad !== null && <EtiquetaCaducidad fechaCaducidad={l.fechaCaducidad} caducidad={l.caducidad} />}</span>}
      acciones={acciones.length > 0 ? <BarraAcciones etiqueta="Acciones del lote">{acciones}</BarraAcciones> : undefined}
      tabs={<Pestanas opciones={pestanas} actual={tab} fijar={(k) => setParams({ tab: k })} />}>
      {error !== null && <ErrorCaja error={error} />}
      <Panel estado={lote}>
        {(d) => (
          <>
            {d.bloqueo != null && (
              <p className="nota error" role="status"><strong>Lote bloqueado:</strong> no puede salir, consumirse ni trasladarse. Motivo: {d.bloqueo.motivo}
                <span className="suave pequena"> · {d.bloqueo.por.nombre} · {fechaHora(d.bloqueo.en)}</span></p>
            )}
            {d.caducidad.estado === 'CADUCADO' && <p className="nota aviso" role="status">Lote caducado. Puede seguir moviéndose (no se bloquea automáticamente); si no debe usarse, bloquéalo.</p>}
            {tab === 'info' && d.situacion === 'SIN_ENTRADAS' && (
              <p className="nota info" role="status"><strong>Lote sin entradas.</strong> {TEXTO_LOTE_SIN_STOCK}
                {puede('operaciones_stock:crear') && <> <Link to="/inventario/operaciones/nueva?tipo=ENTRADA">Registrar una entrada</Link></>}</p>
            )}
            {tab === 'info' && d.situacion === 'AGOTADO' && (
              <p className="nota" role="status"><strong>Lote agotado.</strong> Tuvo stock, pero hoy está a 0 en tus almacenes. Su histórico se conserva.</p>
            )}
            {tab === 'info' && (
              <>
                <div className="panel">
                  <div className="panel-cuerpo datos">
                    <Dato e="Artículo" v={<Link to={`/inventario/articulos/${d.articulo.id}?tab=lotes`}>{nombreArticulo(d.articulo)}</Link>} />
                    <Dato e="Fabricación" v={fecha(d.fechaFabricacion)} />
                    <Dato e="Caducidad" v={d.fechaCaducidad === null ? null : <EtiquetaCaducidad fechaCaducidad={d.fechaCaducidad} caducidad={d.caducidad} />} />
                    <Dato e="Recepción" v={d.fechaRecepcion == null ? null : <span title="Primera entrada del lote en el histórico">{fecha(d.fechaRecepcion)}</span>} />
                    <Dato e="Proveedor" v={d.proveedor ?? null} />
                    <Dato e="Stock (tu ámbito)" v={`${d.stock} ${d.articulo.unidad}`} />
                    {d.articulo.controlaSeries && <Dato e="Series" v={String(d.nSeries)} />}
                    <Dato e="Observaciones" v={d.observaciones} />
                    <Dato e="Alta" v={`${d.creadoPor.nombre} · ${fechaHora(d.creadoEn)}`} />
                  </div>
                </div>
                <section className="panel" aria-label="Stock por almacén">
                  <div className="panel-cuerpo">
                    <h2 className="titulo-seccion">Stock por almacén</h2>
                    {d.stockPorAlmacen.length === 0 ? <p className="suave">{d.situacion === 'SIN_ENTRADAS' ? 'Todavía no ha entrado stock con este lote.' : 'Sin existencias en tus almacenes.'}</p> : (
                      <div className="tabla-desliza">
                        <table aria-label="Stock del lote por almacén" className="tabla-tarjetas">
                          <thead><tr><th>Almacén</th><th className="num">Saldo</th></tr></thead>
                          <tbody>
                            {d.stockPorAlmacen.map((s) => (
                              <tr key={s.almacen.id}><td className="celda-titulo"><Link to={`/inventario/almacenes/${s.almacen.id}?tab=stock`}>{codigoAlmacen(s.almacen)} · {s.almacen.descripcion}</Link></td>
                                <td className="num" data-label="Saldo">{s.saldo} {d.articulo.unidad}</td></tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </section>
                <IdentificadoresLote lote={d} alCambiar={lote.recargar} />
              </>
            )}
            {tab === 'etiqueta' && puede('lotes:etiquetas') && (
              <div className="panel"><div className="panel-cuerpo">
                <PanelEtiqueta ruta={`/api/inventario/etiquetas/lote/${d.id}`} />
                <button type="button" className="principal" style={{ marginTop: 12 }} onClick={() => setImprimir(true)}>Imprimir etiquetas…</button>
              </div></div>
            )}
            {tab === 'series' && <ListaSeries idLote={d.id} />}
            {tab === 'ledger' && puede('movimientos_stock:leer') && <Ledger idLote={d.id} />}
            {tab === 'historial' && <Cambios ruta={`/api/inventario/lotes/${d.id}/historial`} />}
          </>
        )}
      </Panel>
      {editar && l !== null && <EditarLote l={l} cerrar={() => setEditar(false)} alGuardar={() => { setEditar(false); lote.recargar(); }} />}
      {bloqueo !== null && l !== null && <DialogoBloqueo lote={l} bloquear={bloqueo} cerrar={() => setBloqueo(null)} alHacer={() => { setBloqueo(null); lote.recargar(); }} />}
      {imprimir && l !== null && <DialogoEtiquetas elementos={[{ tipo: 'LOT', id: l.id, titulo: `${l.codigo} · ${l.articulo.codigo}` }]} cerrar={() => setImprimir(false)} />}
    </Shell>
  );
}

const TIPOS_IDENTIFICADOR: readonly (readonly [TipoIdentificadorLote, string])[] = [
  ['CODE128', 'Code 128'], ['CODE39', 'Code 39'], ['QR', 'QR'], ['DATAMATRIX', 'Data Matrix'], ['OTRO', 'Otro'],
];

/** F13.2: códigos EXTERNOS que identifican este lote (p. ej. la etiqueta del proveedor). Nunca GTIN: eso es del artículo. */
function IdentificadoresLote({ lote, alCambiar }: { lote: LoteDetalle; alCambiar: () => void }): ReactNode {
  const { puede, usuario } = useSesion();
  //  F13.3.1: tipo '' = automático; elegirlo es una opción técnica (sólo ADMIN).
  const [nuevo, setNuevo] = useState<{ tipo: TipoIdentificadorLote | ''; valor: string }>({ tipo: '', valor: '' });
  const esAdmin = usuario?.roles.some((r) => r.rol === 'ADMIN') === true;
  const [baja, setBaja] = useState<{ id: string; valor: string } | null>(null);
  const [motivo, setMotivo] = useState('');
  const [error, setError] = useState<unknown>(null);
  const gestiona = puede('lotes:gestionar') && lote.activo;
  const lista = lote.identificadores ?? [];
  async function ejecutar(f: () => Promise<unknown>): Promise<boolean> {
    setError(null);
    try { await f(); alCambiar(); return true; } catch (e) { setError(e); return false; }
  }
  return (
    <section className="panel" aria-label="Códigos del lote">
      <div className="panel-cuerpo">
        <h2 className="titulo-seccion">Códigos del lote</h2>
        <p className="suave pequena">Códigos de terceros que identifican este lote concreto (la etiqueta del proveedor, por ejemplo). Los EAN/GTIN identifican el artículo y van en su ficha; la etiqueta interna de NeatOS no hace falta registrarla.</p>
        {error !== null && <ErrorCaja error={error} />}
        {lista.length === 0 ? <p className="suave">Sin códigos externos.</p> : (
          <ul>{lista.map((x) => (
            <li key={x.id}><span className="mono">{x.valor}</span> <span className="suave pequena">{x.tipo}</span> {x.activo ? (gestiona && (
              <button type="button" onClick={() => { setMotivo(''); setBaja({ id: x.id, valor: x.valor }); }} aria-label={`Desactivar el código ${x.valor}`}>Desactivar</button>
            )) : <span className="etiqueta neutra" title={x.motivoDesactivacion ?? undefined}>Desactivado</span>}</li>
          ))}</ul>
        )}
        {gestiona && (
          <form className="fila" style={{ flexWrap: 'wrap', alignItems: 'flex-end', gap: 8 }} aria-label="Nuevo código del lote"
            onSubmit={(ev) => {
              ev.preventDefault();
              const cuerpo = { valor: nuevo.valor, ...(nuevo.tipo === '' ? {} : { tipo: nuevo.tipo }) };
              void ejecutar(() => api.post(`/api/inventario/lotes/${lote.id}/identificadores`, cuerpo)).then((ok) => { if (ok) setNuevo({ tipo: '', valor: '' }); });
            }}>
            <label className="campo"><span>Código</span><input maxLength={200} value={nuevo.valor} onChange={(ev) => setNuevo({ ...nuevo, valor: ev.target.value })} /></label>
            <div className="campo campo-accion"><label aria-hidden="true">&nbsp;</label>
              <button type="submit" disabled={nuevo.valor.trim() === ''}>Añadir código</button></div>
            {esAdmin && (
              <details style={{ flexBasis: '100%' }}>
                <summary className="pequena">Opciones técnicas</summary>
                <label className="campo" style={{ maxWidth: 260 }}><span>Tipo técnico del código</span>
                  <select value={nuevo.tipo} onChange={(ev) => setNuevo({ ...nuevo, tipo: ev.target.value as TipoIdentificadorLote | '' })}>
                    <option value="">Automático (recomendado)</option>
                    {TIPOS_IDENTIFICADOR.map(([k, t]) => <option key={k} value={k}>{t}</option>)}
                  </select></label>
              </details>
            )}
          </form>
        )}
      </div>
      {baja !== null && (
        <Modal titulo={`Desactivar el código ${baja.valor}`} cerrar={() => setBaja(null)}
          pie={<><button type="button" onClick={() => setBaja(null)}>Cancelar</button>
            <button type="button" className="peligro" disabled={motivo.trim().length < 5}
              onClick={() => void ejecutar(() => api.post(`/api/inventario/lotes/${lote.id}/identificadores/${baja.id}/desactivar`, { motivo: motivo.trim() })).then((ok) => { if (ok) setBaja(null); })}>Desactivar</button></>}>
          <label className="campo"><span>Motivo</span><textarea rows={2} maxLength={300} value={motivo} onChange={(ev) => setMotivo(ev.target.value)} /></label>
        </Modal>
      )}
    </section>
  );
}

export function PaginaSerie(): ReactNode {
  const { id = '' } = useParams();
  const { puede } = useSesion();
  const [params, setParams] = useSearchParams();
  const tab = params.get('tab') ?? 'info';
  const serie = useDatos(() => api.get<SerieDetalle>(`/api/inventario/series/${encodeURIComponent(id)}`), [id]);
  const [error, setError] = useState<unknown>(null);
  const s = serie.datos;
  const migas = [{ texto: 'Inventario' }, { texto: 'Stock', a: '/inventario/stock' }, { texto: 'Por serie', a: '/inventario/stock?tab=series' }, { texto: s?.numero ?? '…' }];
  if (serie.error instanceof FalloApi && (serie.error.estado === 404 || serie.error.estado === 403)) {
    return <Shell migas={migas} titulo="Serie no disponible"><div className="panel"><Vacio titulo="Esta serie no existe o no tienes acceso"><Link to="/inventario/stock?tab=series">Volver</Link></Vacio></div></Shell>;
  }
  async function activar(activo: boolean): Promise<void> {
    if (s === null) return;
    setError(null);
    try {
      await api.patch(`/api/inventario/series/${s.id}`, { activo, versionEsperada: s.actualizadoEn });
      serie.recargar();
    } catch (e) {
      setError(e);
    }
  }
  const pestanas: [string, string][] = [['info', 'Información'], ...(puede('movimientos_stock:leer') ? [['ledger', 'Histórico'] as [string, string]] : []), ['historial', 'Cambios']];
  return (
    <Shell dominio="articulos" migas={migas} titulo={s === null ? 'Serie' : `Serie ${s.numero}`}
      subtitulo={s === null ? undefined : <span className="fila" style={{ flexWrap: 'wrap' }}><span>{nombreArticulo(s.articulo)}</span><SituacionSerie s={s} /><Activo v={s.activo} /></span>}
      acciones={s !== null && puede('series:gestionar') ? (
        <BarraAcciones etiqueta="Acciones de la serie">
          <button onClick={() => void activar(!s.activo)}>{s.activo ? 'Desactivar' : 'Reactivar'}</button>
        </BarraAcciones>
      ) : undefined}
      tabs={<Pestanas opciones={pestanas} actual={tab} fijar={(k) => setParams({ tab: k })} />}>
      {error !== null && <ErrorCaja error={error} />}
      <Panel estado={serie}>
        {(d) => (
          <>
            {tab === 'info' && (
              <div className="panel">
                <div className="panel-cuerpo datos">
                  <Dato e="Artículo" v={<Link to={`/inventario/articulos/${d.articulo.id}?tab=series`}>{nombreArticulo(d.articulo)}</Link>} />
                  <Dato e="Lote" v={d.lote === null ? null : <><Link className="mono" to={`/inventario/lotes/${d.lote.id}`}>{d.lote.codigo}</Link> <EtiquetaCaducidad fechaCaducidad={d.lote.fechaCaducidad} caducidad={d.lote.caducidad} /></>} />
                  <Dato e="Ubicación" v={d.estado.situacion === 'EN_STOCK' && d.estado.almacen !== null
                    ? <Link to={`/inventario/almacenes/${d.estado.almacen.id}?tab=stock`}>{codigoAlmacen(d.estado.almacen)} · {d.estado.almacen.descripcion}</Link>
                    : d.estado.situacion === 'FUERA' ? 'Fuera de stock' : 'Sin información en tu ámbito'} />
                  <Dato e="Último movimiento" v={fechaHora(d.ultimoMovimiento)} />
                  <Dato e="Observaciones" v={d.observaciones} />
                  <Dato e="Alta" v={`${d.creadoPor.nombre} · ${fechaHora(d.creadoEn)}`} />
                </div>
              </div>
            )}
            {tab === 'ledger' && puede('movimientos_stock:leer') && <Ledger idSerie={d.id} />}
            {tab === 'historial' && <Cambios ruta={`/api/inventario/series/${d.id}/historial`} />}
          </>
        )}
      </Panel>
    </Shell>
  );
}
